import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { restaurantConfig } from "../config/restaurantConfig";
import Reveal from "../components/Reveal";
import SectionEyebrow from "../components/SectionEyebrow";

export default function Home() {
  const { hero, story, highlights, testimonials, ui } = restaurantConfig;
  const h = ui.home;
  return (
    <div data-testid="page-home">
      {/* ─── HERO (slider) ────────────────────────────────────────────────── */}
      <HeroSlider hero={hero} />

      {/* ─── NOTRE HISTOIRE ───────────────────────────────────────────────── */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 items-center">
          <Reveal>
            <SectionEyebrow>{story.eyebrow}</SectionEyebrow>
            <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl mt-6 whitespace-pre-line leading-[1.05] italic" style={{ fontWeight: 400 }}>
              {story.title}
            </h2>
            <div className="gold-divider w-24 my-8" />
            <div className="space-y-5 text-foreground/80 text-base sm:text-lg font-sans font-light leading-relaxed max-w-xl">
              {story.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
            </div>
            <p className="mt-8 font-serif italic text-accent-line text-xl">— {story.signature}</p>
          </Reveal>

          <Reveal delay={0.15} className="relative">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img src={story.image} alt="Notre histoire" className="w-full h-full object-cover img-cinematic" />
            </div>
            <div className="absolute -bottom-6 -left-6 hidden md:block px-6 py-4 bg-background border border-accent-line">
              <p className="text-[10px] tracking-luxe uppercase text-muted-foreground">{h.historyMaisonLabel}</p>
              <p className="font-serif text-2xl text-accent-line italic">{restaurantConfig.brand.established}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ─── CHEF'S HIGHLIGHTS ────────────────────────────────────────────── */}
      <section className="py-24 md:py-32 bg-card/40 border-y border-border/40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <Reveal>
              <SectionEyebrow>{highlights.eyebrow}</SectionEyebrow>
              <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl mt-6 max-w-2xl italic" style={{ fontWeight: 400 }}>
                {highlights.title}
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <Link
                to="/menu"
                data-testid="highlights-menu-link"
                className="inline-flex items-center gap-3 text-[11px] tracking-luxe uppercase text-accent-line hover:gap-5 transition-all"
              >
                {h.highlightsCta} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {highlights.items.map((item, i) => (
              <Reveal key={item.name} delay={i * 0.1}>
                <article
                  data-testid={`highlight-card-${i}`}
                  className="group relative overflow-hidden bg-background border border-border/50 hover:border-primary/60 transition-colors h-full"
                >
                  <div className="relative aspect-[4/5] overflow-hidden">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-cover img-cinematic transition-transform duration-[1200ms] group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <span className="absolute top-4 left-4 px-3 py-1 text-[10px] tracking-luxe-sm uppercase bg-gold text-zinc-950 font-medium shadow-md">
                      {item.tag}
                    </span>
                  </div>
                  <div className="p-7">
                    <h3 className="font-serif text-2xl mb-3 italic" style={{ fontWeight: 400 }}>
                      {item.name}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed font-sans font-light">
                      {item.description}
                    </p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─── TESTIMONIALS SLIDER ──────────────────────────────────────────── */}
      <TestimonialsSlider items={testimonials} />

      {/* ─── BANNER CTA ───────────────────────────────────────────────────── */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src={restaurantConfig.images.atmosphere[2]} alt="" className="w-full h-full object-cover img-cinematic" />
          <div className="absolute inset-0 bg-black/65" />
        </div>
        <div className="relative max-w-3xl mx-auto px-6 text-center text-[#FDFBF7]">
          <Reveal>
            <SectionEyebrow className="justify-center [&_span:first-child]:bg-zinc-100 [&_span:last-child]:text-zinc-100 inline-flex">
              {h.bannerEyebrow}
            </SectionEyebrow>
            <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl mt-6 italic" style={{ fontWeight: 400 }}>
              {h.bannerTitle}
            </h2>
            <p className="mt-6 text-[#FDFBF7]/80 max-w-xl mx-auto font-sans font-light">
              {h.bannerBody}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/reservation"
                data-testid="banner-reserve-cta"
                className="px-8 py-4 bg-burgundy text-white text-xs tracking-luxe uppercase hover:bg-burgundy-hover transition-colors"
              >
                {restaurantConfig.ui.reserveLongCta}
              </Link>
              <a
                href={restaurantConfig.contact.phoneLink}
                className="px-8 py-4 border border-[#FDFBF7]/40 text-[#FDFBF7] text-xs tracking-luxe uppercase hover:border-gold hover:text-gold transition-colors"
              >
                {restaurantConfig.contact.phoneDisplay}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
function HeroSlider({ hero }) {
  const slides = hero.heroImages?.length ? hero.heroImages : [hero.image];
  const [idx, setIdx] = useState(0);
  const interval = hero.slideIntervalMs ?? 4500;

  useEffect(() => {
    if (slides.length < 2) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % slides.length), interval);
    return () => clearInterval(id);
  }, [slides.length, interval]);

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-end overflow-hidden bg-zinc-950"
    >
      {/* Crossfade slider */}
      <div className="absolute inset-0">
        <AnimatePresence initial={false} mode="sync">
          <motion.img
            key={slides[idx]}
            src={slides[idx]}
            alt="La Brasserie Des Artistes"
            data-testid={`hero-slide-${idx}`}
            initial={{ opacity: 0, scale: 1.06 }}
            animate={{ opacity: 1, scale: 1.0 }}
            exit={{ opacity: 0, scale: 1.04 }}
            transition={{ opacity: { duration: 1.6, ease: "easeInOut" }, scale: { duration: 6, ease: "easeOut" } }}
            className="absolute inset-0 w-full h-full object-cover img-cinematic will-change-transform"
          />
        </AnimatePresence>
        {/* Permanent dark gradient overlay for legibility — softer */}
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/35 via-zinc-950/30 to-zinc-950/70" />
        <div className="absolute inset-0 bg-gradient-to-r from-zinc-950/60 via-zinc-950/20 to-transparent" />
      </div>

      {/* Slide indicators */}
      {slides.length > 1 && (
        <div className="absolute top-32 right-6 md:right-10 z-10 flex flex-col gap-2.5">
          {slides.map((_, i) => (
            <button
              key={i}
              data-testid={`hero-slide-dot-${i}`}
              onClick={() => setIdx(i)}
              aria-label={`Image ${i + 1}`}
              className={`h-px transition-all duration-500 ${
                i === idx ? "w-10 bg-burgundy" : "w-5 bg-white/40 hover:bg-white/80"
              }`}
            />
          ))}
        </div>
      )}

      {/* Content */}
      <div className="relative w-full max-w-[1400px] mx-auto px-6 md:px-10 pb-24 md:pb-32 z-10">
        <motion.div
          initial="initial"
          animate="animate"
          variants={{
            initial: {},
            animate: { transition: { staggerChildren: 0.18, delayChildren: 0.15 } },
          }}
          className="max-w-3xl"
        >
          <motion.div
            variants={{
              initial: { opacity: 0, y: 18 },
              animate: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
            }}
          >
            <SectionEyebrow className="[&_span:first-child]:bg-zinc-100 [&_span:last-child]:text-zinc-100">
              {hero.eyebrow}
            </SectionEyebrow>
          </motion.div>

          <motion.h1
            data-testid="hero-title"
            variants={{
              initial: { opacity: 0, y: 28 },
              animate: { opacity: 1, y: 0, transition: { duration: 1.0, ease: [0.22, 1, 0.36, 1] } },
            }}
            className="font-serif text-zinc-50 text-5xl sm:text-6xl md:text-7xl lg:text-8xl mt-6 leading-[1.02] whitespace-pre-line italic drop-shadow-[0_4px_24px_rgba(0,0,0,0.6)]"
            style={{ fontWeight: 400 }}
          >
            {hero.title}
          </motion.h1>

          <motion.p
            variants={{
              initial: { opacity: 0, y: 18 },
              animate: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
            }}
            className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-zinc-200 font-sans font-light"
          >
            {hero.subtitle}
          </motion.p>

          <motion.div
            variants={{
              initial: { opacity: 0, y: 18 },
              animate: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
            }}
            className="mt-12 flex flex-col sm:flex-row gap-4"
          >
            <Link
              to={hero.ctaPrimary.to}
              data-testid="hero-primary-cta"
              className="group inline-flex items-center gap-3 px-8 py-4 bg-burgundy text-white text-xs tracking-luxe uppercase hover:bg-burgundy-hover hover:scale-[1.03] transition-all duration-300 shadow-lg shadow-burgundy/30"
            >
              {hero.ctaPrimary.label}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
            </Link>
            <Link
              to={hero.ctaSecondary.to}
              data-testid="hero-secondary-cta"
              className="group inline-flex items-center gap-3 px-8 py-4 border border-zinc-100/60 text-zinc-100 text-xs tracking-luxe uppercase hover:bg-white hover:text-burgundy hover:border-white hover:scale-[1.03] transition-all duration-300 backdrop-blur-sm bg-white/5"
            >
              {hero.ctaSecondary.label}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
            </Link>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 0.8 }}
        className="absolute bottom-8 right-8 hidden md:flex items-center gap-3 text-[10px] tracking-luxe text-zinc-300 uppercase z-10"
      >
        <span>{restaurantConfig.ui.scrollIndicator}</span>
        <motion.span
          animate={{ scaleY: [1, 0.3, 1] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="block w-px h-10 bg-zinc-300/60 origin-top"
        />
      </motion.div>
    </section>
  );
}

// ───────────────────────────────────────────────────────────────────────────
function TestimonialsSlider({ items }) {
  const home = restaurantConfig.ui.home;
  const [index, setIndex] = useState(0);
  const count = items.length;

  useEffect(() => {
    const id = setInterval(() => setIndex((i) => (i + 1) % count), 7000);
    return () => clearInterval(id);
  }, [count]);

  const go = (dir) => setIndex((i) => (i + dir + count) % count);

  return (
    <section data-testid="testimonials" className="py-24 md:py-32">
      <div className="max-w-[1100px] mx-auto px-6 md:px-10 text-center">
        <SectionEyebrow className="justify-center inline-flex">{home.testimonialsEyebrow}</SectionEyebrow>
        <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl mt-6 italic" style={{ fontWeight: 400 }}>
          {home.testimonialsTitle}
        </h2>

        <div className="relative mt-16 min-h-[280px]">
          <Quote className="w-12 h-12 text-gold/60 mx-auto mb-6" strokeWidth={1} />
          <AnimatePresence mode="wait">
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`testimonial-${index}`}
            >
              <p className="font-serif italic text-2xl sm:text-3xl md:text-4xl leading-snug text-foreground/90 max-w-3xl mx-auto" style={{ fontWeight: 400 }}>
                « {items[index].quote} »
              </p>
              <div className="mt-10">
                <p className="text-sm tracking-luxe-sm uppercase text-accent-line">{items[index].author}</p>
                <p className="text-xs text-muted-foreground mt-1 font-sans">{items[index].role}</p>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="flex items-center justify-center gap-6 mt-12">
          <button
            data-testid="testimonial-prev"
            onClick={() => go(-1)}
            className="w-11 h-11 inline-flex items-center justify-center rounded-full border border-border/60 hover:border-gold hover:text-gold transition-colors"
            aria-label={home.testimonialsPrevAria}
          >
            <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
          </button>
          <div className="flex gap-2">
            {items.map((_, i) => (
              <button
                key={i}
                data-testid={`testimonial-dot-${i}`}
                onClick={() => setIndex(i)}
                aria-label={`Témoignage ${i + 1}`}
                className={`h-px transition-all duration-500 ${i === index ? "w-10 bg-gold" : "w-5 bg-border"}`}
              />
            ))}
          </div>
          <button
            data-testid="testimonial-next"
            onClick={() => go(1)}
            className="w-11 h-11 inline-flex items-center justify-center rounded-full border border-border/60 hover:border-gold hover:text-gold transition-colors"
            aria-label={home.testimonialsNextAria}
          >
            <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </section>
  );
}
